import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Trophy, 
  Star, 
  Target, 
  PiggyBank, 
  TrendingUp, 
  Award,
  Medal,
  Crown,
  Wallet,
  Users,
  Lock
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';
import WhatsAppIntegration from '@/components/profile/WhatsAppIntegration';
import { useGamification } from '@/contexts/GamificationContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Achievement {
  id: string;
  title: string;
  description: string;
  points: number;
  isUnlocked: boolean;
  progress: number;
  target: number;
  icon?: React.ReactNode;
}

interface LeaderboardUser {
  id: string;
  name: string;
  level: number;
  xp: number;
  achievements: number;
}

const achievementIcons: Record<string, React.ReactNode> = {
  first_wallet: <Wallet className="h-6 w-6" />,
  wallet_master: <Users className="h-6 w-6" />,
  savings_master: <PiggyBank className="h-6 w-6" />,
  active_wallets: <Star className="h-6 w-6" />,
};

const Profile = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { level, xp, nextLevelXp, achievements, checkAchievements, unlockedRewards } = useGamification();
  const [userData, setUserData] = React.useState<{ full_name: string } | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardUser[]>([]);

  useEffect(() => {
    const loadUserData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', user.id)
          .single();
        
        if (data) {
          setUserData(data);
        }
      }
    };

    loadUserData();
    checkAchievements();
    loadLeaderboard();
  }, []);

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/login');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const loadLeaderboard = async () => {
    try {
      const { data, error } = await supabase
        .from('user_gamification')
        .select(`
          user_id,
          level,
          xp,
          achievements,
          profiles:user_id (
            name
          )
        `)
        .order('xp', { ascending: false })
        .limit(10);

      if (error) throw error;

      const formattedData = data.map((item: any) => ({
        id: item.user_id,
        name: item.profiles?.name || 'Anonymous',
        level: item.level,
        xp: item.xp,
        achievements: item.achievements.filter((a: Achievement) => a.isUnlocked).length
      }));

      setLeaderboard(formattedData);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    }
  };

  const formatAchievements = (achievements: any[]): Achievement[] => {
    return achievements.map(achievement => ({
      ...achievement,
      icon: achievementIcons[achievement.id] || <Trophy className="h-6 w-6" />
    }));
  };

  const AchievementCard = ({ achievement }: { achievement: Achievement }) => {
    const progress = Math.min((achievement.progress / achievement.target) * 100, 100);
    const isLocked = !achievement.isUnlocked;

    return (
      <Card className="relative overflow-hidden">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              {isLocked ? (
                <Lock className="h-5 w-5 text-muted-foreground" />
              ) : (
                <Trophy className="h-5 w-5 text-yellow-500" />
              )}
              {achievement.title}
            </CardTitle>
            <Badge className={isLocked ? "bg-muted text-muted-foreground" : "bg-primary text-primary-foreground"}>
              {isLocked ? "Locked" : "Unlocked"}
            </Badge>
          </div>
          <CardDescription>{achievement.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Progress</span>
              <span>{achievement.progress}/{achievement.target}</span>
            </div>
            <Progress 
              value={progress} 
              className={cn(
                "h-2",
                isLocked ? "bg-muted" : "bg-primary"
              )}
            />
          </div>
        </CardContent>
        <CardFooter>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Star className="h-4 w-4" />
            <span>{achievement.points} XP</span>
          </div>
        </CardFooter>
      </Card>
    );
  };

  return (
    <div className="container mx-auto py-8 space-y-8">
      <h1 className="text-2xl font-bold">Profile</h1>
      
      <WhatsAppIntegration />
      
      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Account Settings</h2>
          <Button 
            onClick={handleSignOut}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Sign Out
          </Button>
        </div>
      </Card>

      {/* Profile Header */}
      <Card className="p-6">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
              <Crown className="h-10 w-10 text-primary" />
            </div>
            <div className="absolute -bottom-2 -right-2 bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-xs font-semibold">
              Level {level}
            </div>
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold">{userData?.full_name || 'User'}</h2>
            <p className="text-muted-foreground">Financial Explorer</p>
          </div>
        </div>
      </Card>

      {/* Level Progress */}
      <Card className="p-6">
        <div className="space-y-2">
          <div className="flex justify-between">
            <h3 className="font-medium">Level Progress</h3>
            <span className="text-sm text-muted-foreground">
              {xp}/{nextLevelXp} XP
            </span>
          </div>
          <Progress value={(xp / nextLevelXp) * 100} className="h-2" />
        </div>
      </Card>

      {/* Achievements and Leaderboard */}
      <Tabs defaultValue="achievements" className="space-y-4">
        <TabsList>
          <TabsTrigger value="achievements">Achievements</TabsTrigger>
          <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
        </TabsList>
        
        <TabsContent value="achievements" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {achievements.map((achievement) => (
              <AchievementCard key={achievement.id} achievement={achievement} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="leaderboard" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Top Achievers</CardTitle>
              <CardDescription>See how you rank among other users</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {leaderboard.map((user, index) => (
                  <div key={user.id} className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-muted-foreground">Level {user.level}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{user.achievements} achievements</p>
                      <p className="text-sm text-muted-foreground">{user.xp} XP</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Rewards */}
      <Card className="p-6">
        <h3 className="font-medium mb-4">Available Rewards</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-lg border bg-primary/5 border-primary">
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="h-5 w-5 text-primary" />
              <h4 className="font-medium">Premium Features</h4>
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              Unlock advanced analytics and AI insights
            </p>
            <Button 
              className={cn(
                "w-full border border-input bg-background hover:bg-accent hover:text-accent-foreground",
                xp < 500 && "opacity-50 cursor-not-allowed"
              )}
              disabled={xp < 500}
            >
              Unlock (500 XP)
            </Button>
          </div>
          <div className="p-4 rounded-lg border bg-primary/5 border-primary">
            <div className="flex items-center gap-2 mb-2">
              <Award className="h-5 w-5 text-primary" />
              <h4 className="font-medium">Custom Categories</h4>
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              Create your own expense categories
            </p>
            <Button 
              className={cn(
                "w-full border border-input bg-background hover:bg-accent hover:text-accent-foreground",
                xp < 300 && "opacity-50 cursor-not-allowed"
              )}
              disabled={xp < 300}
            >
              Unlock (300 XP)
            </Button>
          </div>
          <div className="p-4 rounded-lg border bg-primary/5 border-primary">
            <div className="flex items-center gap-2 mb-2">
              <Medal className="h-5 w-5 text-primary" />
              <h4 className="font-medium">Export Reports</h4>
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              Export your financial reports
            </p>
            <Button 
              className={cn(
                "w-full border border-input bg-background hover:bg-accent hover:text-accent-foreground",
                xp < 200 && "opacity-50 cursor-not-allowed"
              )}
              disabled={xp < 200}
            >
              Unlock (200 XP)
            </Button>
          </div>
        </div>
      </Card>

      {/* Unlocked Rewards */}
      <Card>
        <CardHeader>
          <CardTitle>Unlocked Rewards</CardTitle>
          <CardDescription>Special rewards you've earned</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {unlockedRewards.map((reward) => (
              <div key={reward} className="p-4 rounded-lg bg-primary/5 border border-primary">
                <div className="flex items-center gap-3">
                  <Crown className="h-5 w-5 text-primary" />
                  <div>
                    <h4 className="font-medium">{reward}</h4>
                    <p className="text-sm text-muted-foreground">Unlocked at level {level}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile; 